import dayjs, { Dayjs } from "dayjs"
import utc from "dayjs/plugin/utc"
import { ProjectType, ProjectTypes, ProjectStatuses } from "../../../common/constants"
import { RescheduleCandidate, ScheduledEvent, SchedulingJob } from "../scheduleApi"
import { Route } from "../../RouteManagement/routingApi"

dayjs.extend(utc)

export const defaultScheduleCreateEventFilters: ScheduleCreateEventFilters = {
  searchQuery: '',
  crewMemberIds: [],
  divisionIds: [],
  projectTypeIds: [],
  showCompleteInactiveJobs: false,
}

export type ScheduleCreateEventFilters = {
  searchQuery: string
  crewMemberIds: number[]
  divisionIds: number[]
  projectTypeIds: number[]
  showCompleteInactiveJobs: boolean
}

export const filterJobs = (jobs: SchedulingJob[], filters: ScheduleCreateEventFilters) => {
  return jobs.filter(job => {
    if (filters.searchQuery) {
      const searchLower = filters.searchQuery.toLowerCase()
      const matchesJob = job.jobName.toLowerCase().includes(searchLower)
      const matchesClient = job.customerDisplayName.toLowerCase().includes(searchLower)
      const matchesJobNumber = job.jobNumber?.toLowerCase().includes(searchLower) ?? false
      if (!matchesJob && !matchesClient && !matchesJobNumber) return false
    }

    if (filters.divisionIds.length > 0) {
      if (!job.approvedWorkAreas.some(wa => wa.divisionId && filters.divisionIds.includes(wa.divisionId))) {
        return false
      }
    }

    if (filters.projectTypeIds.length > 0) {
      if (!job.type || !filters.projectTypeIds.includes(job.type as ProjectType)) {
        return false
      }
    }

    if (!filters.showCompleteInactiveJobs) {
      const isProject = job.type === ProjectTypes.Project
      if (job.status === ProjectStatuses.Completed || (!isProject && !job.active)) {
        return false
      }
    }

    return true
  })
}

export const filterRescheduleCandidates = (candidates: RescheduleCandidate[], filters: ScheduleCreateEventFilters) => {
  return candidates.filter(candidate => {
    if (filters.searchQuery) {
      const searchLower = filters.searchQuery.toLowerCase()
      const matchesJob = candidate.jobName.toLowerCase().includes(searchLower)
      const matchesClient = candidate.customerDisplayName.toLowerCase().includes(searchLower)
      const matchesJobNumber = candidate.jobNumber?.toLowerCase().includes(searchLower) ?? false
      if (!matchesJob && !matchesClient && !matchesJobNumber) {
        return false
      }
    }

    if (filters.divisionIds.length > 0) {
      const candidateDivisionIds = candidate.allDivisionIds
        ? candidate.allDivisionIds.split('|').map(id => parseInt(id)).filter(id => !isNaN(id))
        : []
      if (!candidateDivisionIds.some(id => filters.divisionIds.includes(id))) {
        return false
      }
    }

    if (filters.projectTypeIds.length > 0) {
      if (!candidate.type || !filters.projectTypeIds.includes(candidate.type as ProjectType)) {
        return false
      }
    }

    return true
  })
}

export const rescheduleCandidateToInitialEvent = (candidate: RescheduleCandidate, newStart: Dayjs): ScheduledEvent => {
  const originalStartDayUtc = dayjs.utc(candidate.scheduledStartUtc).startOf('day')
  const newStartDayUtc = dayjs.utc(newStart.format('YYYY-MM-DD'))
  const dayShiftDays = newStartDayUtc.diff(originalStartDayUtc, 'day')
  const shiftedEnd = candidate.allDay
    ? dayjs.utc(candidate.scheduledEndUtc).add(dayShiftDays, 'day').toISOString()
    : candidate.scheduledEndUtc

  return {
    id: candidate.calendarEventId,
    jobId: candidate.jobId,
    jobName: candidate.jobName,
    startUtc: candidate.scheduledStartUtc,
    start: candidate.scheduledStartUtc,
    endUtc: shiftedEnd,
    end: shiftedEnd,
    allDay: candidate.allDay,
    crewForemanEmail: candidate.crewForemanEmail,
    crewMemberIds: candidate.crewMemberIds,
    workAreaIds: candidate.workAreaIds,
    notes: candidate.notes,
    sendCrewLeadNotifications: candidate.notifications,
    schedulingNotes: candidate.schedulingNotes,
    projectType: candidate.type,
    projectAllDivisionIds: candidate.allDivisionIds,
    customerId: candidate.customerId,
    customerDisplayName: candidate.customerDisplayName,
    customerAddress1: candidate.address1,
    customerAddress2: candidate.address2,
    customerCity: candidate.city,
    customerState: candidate.state,
    customerZip: candidate.zip,
    crewForemanColor: '',
    customerUrl: null,
    customerCounty: null,
    customerCountryCode: null,
    propertyNickname: null,
    projectSold: null,
    workAreaNames: null,
    routeId: null,
    routeName: null,
    propertyId: null,
    latitude: null,
    longitude: null,
    repeatingId: null,
    salesPersonEmail: null,
    createdByVisit: false,
    frozen: false,
  }
}

export type RescheduleRangeType = 'last7' | 'last30' | 'custom'

export type RescheduleDateRange = {
  startDate: Dayjs | null
  endDate: Dayjs | null
}

export const getRescheduleCandidatesDateRange = (
  rangeType: RescheduleRangeType,
  customRange: [Dayjs, Dayjs] | null,
  yesterday: Dayjs
): RescheduleDateRange => {
  if (rangeType === 'last7') {
    return { startDate: yesterday.subtract(6, 'day'), endDate: yesterday }
  }
  if (rangeType === 'last30') {
    return { startDate: yesterday.subtract(29, 'day'), endDate: yesterday }
  }
  if (customRange) {
    return { startDate: customRange[0], endDate: customRange[1] }
  }
  return { startDate: null, endDate: null }
}

export const rescheduleCandidateToSchedulingJob = (candidate: RescheduleCandidate): SchedulingJob => ({
  jobId: candidate.jobId,
  jobName: candidate.jobName,
  jobNumber: candidate.jobNumber,
  customerId: candidate.customerId,
  customerDisplayName: candidate.customerDisplayName,
  active: true,
  allDivisionIds: candidate.allDivisionIds,
  type: candidate.type as ProjectType,
  status: undefined,
  address1: candidate.address1,
  address2: candidate.address2,
  city: candidate.city,
  state: candidate.state,
  zip: candidate.zip,
  calendarEventCount: 0,
  hasApprovedWorkareas: true,
  schedulingNotes: candidate.schedulingNotes ?? undefined,
  approvedWorkAreas: [],
})

export const filterRoutes = (routes: Route[], filters: ScheduleCreateEventFilters) => {
  return routes.filter(route => {
    if (filters.searchQuery) {
      const searchLower = filters.searchQuery.toLowerCase()
      const matchesName = route.name.toLowerCase().includes(searchLower)
      if (!matchesName) return false
    }

    if (filters.crewMemberIds.length > 0) {
      const routeCrewMemberIds = route.crewMemberIds ?
        route.crewMemberIds.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id)) : []
      const crewLeadMatches = route.crewLeadId && filters.crewMemberIds.includes(route.crewLeadId)
      const crewMemberMatches = routeCrewMemberIds.some(id => filters.crewMemberIds.includes(id))
      if (!crewLeadMatches && !crewMemberMatches) return false
    }

    return true
  })
}

export const countActiveFilters = (filters: ScheduleCreateEventFilters): number => {
  return [
    filters.divisionIds.length > 0,
    filters.crewMemberIds.length > 0,
    filters.projectTypeIds.length > 0,
    filters.showCompleteInactiveJobs,
  ].filter(Boolean).length
}

export const formatDate = (dateString: string) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

export const formatRelativeTime = (dateString: string) => {
  const date = new Date(dateString)
  const now = new Date()
  const diffInMs = now.getTime() - date.getTime()
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60))
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24))

  if (diffInHours < 1) return 'Less than an hour ago'
  if (diffInHours < 24) return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`
  if (diffInDays < 7) return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`
  return formatDate(dateString)
}